import React, { useEffect, useMemo, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import { Segmented, DatePicker, Alert, Select } from "antd"
import { useQuery } from "@tanstack/react-query"
import { companyService, COMPANY_ENDPOINTS } from "services/companyService"
import SegmentWrapper from "components/custom/SegmentWrapper"
import { Button } from "components"
import FeatureHint from "components/new-stuff/ui/FeatureHint"
import { LeaveTimeline } from "components/new-stuff/leave-timeline"
import LeaveMonthGrid from "components/new-stuff/leave-timeline/LeaveMonthGrid"
import type {
  LeaveTimelineBar,
  LeaveTimelineDayFlag,
  LeaveTimelineRow,
} from "components/new-stuff/leave-timeline"
import {
  ILeaveDepartmentOption,
  leaveService,
  LEAVE_ENDPOINTS,
  ILeaveCalendar,
  LeaveCalendarScope,
} from "services/leaveService"

const ISO = "YYYY-MM-DD"
const API = "DD/MM/YYYY"

const SCOPE_LABELS: Record<LeaveCalendarScope, string> = {
  mine: "My leave",
  team: "My team",
  company: "Company",
  workplace: "Workplace",
  department: "Department",
}

/**
 * Which views each side of the product offers. An admin comes here to look across the
 * organisation; their own leave and their team live on the employee side, which offers
 * exactly those. The server still decides what the viewer may actually request.
 */
const AUDIENCE_SCOPES: Record<LeaveCalendarAudience, LeaveCalendarScope[]> = {
  admin: ["company", "workplace", "department"],
  employee: ["mine", "team"],
}

type LeaveCalendarAudience = "admin" | "employee"

interface IDepartmentOption {
  departmentId: number
  description?: string
}

interface IWorkplaceOption {
  officeAddressId: number
  displayName?: string
}

/**
 * Month and quarter are both calendar grids; the timeline is opt-in.
 *
 * The timeline answers a real question — whose leave overlaps with whose — but it is a Gantt
 * chart, and reading "what does the next three months look like" off ninety narrow columns is
 * work. A quarter is three month grids, which is how people picture a quarter. The timeline is
 * still one click away for anyone coordinating cover, and it remains the default on the
 * planning roster where overlap is the whole point.
 */
type RangeMode = "month" | "quarter" | "timeline"

/**
 * LC. One view of who is away and when.
 *
 * The server decides which scopes this viewer may request and whether leave types are
 * visible; this only renders what comes back. Doing it the other way round would put LC-04
 * in the client, where it is one careless prop away from leaking.
 */
const LeaveCalendar: React.FC<{ audience?: LeaveCalendarAudience }> = ({
  audience = "employee",
}) => {
  const [scope, setScope] = useState<LeaveCalendarScope>(
    AUDIENCE_SCOPES[audience][0]
  )
  const [departmentIds, setDepartmentIds] = useState<number[]>([])
  const [workplaceIds, setWorkplaceIds] = useState<number[]>([])
  const [rangeMode, setRangeMode] = useState<RangeMode>("month")
  const [anchor, setAnchor] = useState<Dayjs>(dayjs())

  const { start, end } = useMemo(() => {
    if (rangeMode === "month") {
      return { start: anchor.startOf("month"), end: anchor.endOf("month") }
    }

    // Computed rather than using startOf("quarter"), which needs dayjs's quarterOfYear
    // plugin -- not extended globally here, so relying on it would work or not depending on
    // which page loaded first.
    const quarterStart = anchor.month(Math.floor(anchor.month() / 3) * 3).startOf("month")
    return { start: quarterStart, end: quarterStart.add(2, "month").endOf("month") }
  }, [anchor, rangeMode])

  const { data: departmentsRes } = useQuery({
    queryKey: [LEAVE_ENDPOINTS.VIEW_LEAVE_DEPARTMENTS],
    queryFn: () => leaveService.getDepartmentOptions(),
    enabled: audience === "admin" && scope === "department",
  })

  const { data: workplacesRes } = useQuery({
    queryKey: [
      COMPANY_ENDPOINTS.GET_OFFICE_ADDRESSES,
      { showActiveOnly: true },
    ],
    queryFn: () => companyService.getOfficeAddresses({ showActiveOnly: true }),
    enabled: audience === "admin" && scope === "workplace",
  })

  const departments: ILeaveDepartmentOption[] =
    (departmentsRes as { data?: ILeaveDepartmentOption[] } | undefined)?.data ?? []
  const workplaces: IWorkplaceOption[] =
    (workplacesRes as { data?: IWorkplaceOption[] } | undefined)?.data ?? []

  // A department or workplace view shows nothing until something is chosen. Left empty,
  // the server would fall back to the admin's own, which is not what they asked to see.
  const needsSelection =
    (scope === "department" && departmentIds.length === 0) ||
    (scope === "workplace" && workplaceIds.length === 0)

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      LEAVE_ENDPOINTS.VIEW_LEAVE_CALENDAR,
      scope,
      scope === "department" ? departmentIds : null,
      scope === "workplace" ? workplaceIds : null,
      start.format(ISO),
      end.format(ISO),
    ],
    queryFn: () =>
      leaveService.getLeaveCalendar({
        scope,
        first_day: start.format(API),
        last_day: end.format(API),
        department_ids:
          scope === "department" ? departmentIds.join(",") : undefined,
        workplace_ids:
          scope === "workplace" ? workplaceIds.join(",") : undefined,
      }),
    enabled: !needsSelection,
    // LC-06: no caching beyond the session, so an approval made elsewhere shows on reload.
    staleTime: 0,
  })

  const calendar: ILeaveCalendar | undefined = needsSelection
    ? undefined
    : data?.data
  const loading = !needsSelection && isLoading

  // The server is the authority on which scopes this viewer may request; the audience
  // decides which of those belong on this side of the product. Someone on the admin side
  // without permission to look across leave falls back to whatever they are allowed.
  const available = calendar?.available_scopes ?? AUDIENCE_SCOPES[audience]
  const offered = AUDIENCE_SCOPES[audience].filter((s) => available.includes(s))
  const scopeOptions = (offered.length ? offered : available).map((s) => ({
    label: SCOPE_LABELS[s] ?? s,
    value: s,
  }))

  // When the server narrowed the request to something this viewer may see, follow it, so
  // the picker never names a view the calendar is not showing.
  const servedScope = data?.data?.scope
  useEffect(() => {
    if (servedScope && servedScope !== scope && !available.includes(scope)) {
      setScope(servedScope)
    }
  }, [servedScope, scope, available])

  const { rows, bars, dayFlags } = useMemo(
    () => buildTimeline(calendar),
    [calendar]
  )

  return (
    <SegmentWrapper title="Leave calendar">
      <div className="px-4 md:px-6 lg:px-8 xl:px-[60px] pb-6 flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={scope}
            onChange={(v) => setScope(v)}
            options={scopeOptions}
            className="min-w-[160px]"
          />

          {scope === "department" ? (
            <Select
              mode="multiple"
              value={departmentIds}
              onChange={setDepartmentIds}
              allowClear
              showSearch
              maxTagCount="responsive"
              optionFilterProp="label"
              placeholder="Choose departments"
              className="min-w-[240px] max-w-[420px]"
              options={departments.map((d) => ({
                label: d.description || String(d.departmentId),
                value: d.departmentId,
              }))}
            />
          ) : null}

          {scope === "workplace" ? (
            <Select
              mode="multiple"
              value={workplaceIds}
              onChange={setWorkplaceIds}
              allowClear
              showSearch
              maxTagCount="responsive"
              optionFilterProp="label"
              placeholder="Choose workplaces"
              className="min-w-[240px] max-w-[420px]"
              options={workplaces.map((w) => ({
                label: w.displayName || String(w.officeAddressId),
                value: w.officeAddressId,
              }))}
            />
          ) : null}

          <FeatureHint className="mr-1">
            Month and quarter show a calendar. Timeline shows the same period as bars across
            days, which is the quickest way to see whose leave overlaps with whose when you are
            arranging cover.
          </FeatureHint>

          <Segmented
            value={rangeMode}
            onChange={(v) => setRangeMode(v as RangeMode)}
            options={[
              { label: "Month", value: "month" },
              { label: "Quarter", value: "quarter" },
              { label: "Timeline", value: "timeline" },
            ]}
          />

          <div className="flex items-center gap-1">
            <Button
              color="neutral"
              onClick={() =>
                setAnchor(anchor.subtract(rangeMode === "month" ? 1 : 3, "month"))
              }
              title="Previous"
            >
              ‹
            </Button>
            <DatePicker
              picker={rangeMode === "month" ? "month" : "quarter"}
              value={anchor}
              onChange={(v) => v && setAnchor(v)}
              allowClear={false}
            />
            <Button
              color="neutral"
              onClick={() =>
                setAnchor(anchor.add(rangeMode === "month" ? 1 : 3, "month"))
              }
              title="Next"
            >
              ›
            </Button>
            {!anchor.isSame(dayjs(), "month") ? (
              <Button
                color="neutral"
                onClick={() => setAnchor(dayjs())}
              >
                Today
              </Button>
            ) : null}
          </div>

          <span className="font-avenir text-xs text-padeSubtext ml-auto tabular-nums">
            {calendar?.entries?.length
              ? `${calendar.entries.length} ${
                  calendar.entries.length === 1 ? "absence" : "absences"
                } · ${start.format("DD MMM")} – ${end.format("DD MMM YYYY")}`
              : `${start.format("DD MMM")} – ${end.format("DD MMM YYYY")}`}
          </span>
        </div>

        {isError ? (
          <Alert
            type="warning"
            showIcon
            message="Could not load the calendar"
            description="Try a shorter range, or reload the page."
          />
        ) : null}

        {calendar && !calendar.can_see_leave_types && scope !== "mine" ? (
          <Alert
            type="info"
            showIcon
            message="Colleagues' leave shows as dates only"
            description="You can see when people are away. Seeing which type of leave they are on needs additional permission."
          />
        ) : null}

        {rangeMode === "month" ? (
          <LeaveMonthGrid
            month={anchor}
            entries={calendar?.entries ?? []}
            days={calendar?.days ?? []}
            loading={loading}
            showLeaveType={calendar?.can_see_leave_types !== false}
            emptyMessage={
              needsSelection
                ? `Choose ${scope === "department" ? "departments" : "workplaces"} to see who is away.`
                : "Nobody is away this month."
            }
          />
        ) : rangeMode === "quarter" ? (
          // Three grids side by side on a wide screen, stacked on a narrow one. Each gets the
          // whole quarter's entries and filters to its own days, which costs nothing at this
          // size and keeps the grid component unaware of quarters.
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {[0, 1, 2].map((offset) => {
              const monthAnchor = start.add(offset, "month")
              return (
                <div
                  key={monthAnchor.format("YYYY-MM")}
                  className="flex flex-col gap-2 min-w-0"
                >
                  <h4 className="font-circular text-sm font-semibold text-padeBlack m-0">
                    {monthAnchor.format("MMMM YYYY")}
                  </h4>
                  <LeaveMonthGrid
                    month={monthAnchor}
                    entries={calendar?.entries ?? []}
                    days={calendar?.days ?? []}
                    loading={loading}
                    showLeaveType={calendar?.can_see_leave_types !== false}
                    compact
                    emptyMessage=""
                  />
                </div>
              )
            })}
          </div>
        ) : (
          <LeaveTimeline
            rows={rows}
            bars={bars}
            dayFlags={dayFlags}
            startDate={start.format(ISO)}
            endDate={end.format(ISO)}
            loading={loading}
            emptyMessage={
              needsSelection
                ? `Choose ${scope === "department" ? "departments" : "workplaces"} to see who is away.`
                : "Nobody is away in this period."
            }
          />
        )}
      </div>
    </SegmentWrapper>
  )
}

function buildTimeline(calendar: ILeaveCalendar | undefined) {
  const rows: LeaveTimelineRow[] = []
  const bars: LeaveTimelineBar[] = []
  const seen = new Set<string | number>()

  calendar?.entries?.forEach((entry) => {
    const rowId = entry.person_id ?? entry.employee_request_id

    if (!seen.has(rowId)) {
      seen.add(rowId)
      rows.push({
        id: rowId,
        name: entry.full_name ?? "Employee",
        subtitle: entry.department ?? undefined,
      })
    }

    const startDate = toIso(entry.first_day)
    const endDate = toIso(entry.last_day)

    if (startDate && endDate) {
      bars.push({
        id: entry.employee_request_id,
        rowId,
        startDate,
        endDate,
        kind: entry.kind,
        // Whatever the server decided this viewer may see -- never re-derived here.
        label: `${entry.full_name ?? "Employee"} — ${entry.label ?? "On leave"}`,
      })
    }
  })

  const dayFlags = (calendar?.days ?? []).reduce<LeaveTimelineDayFlag[]>((acc, day) => {
    const date = toIso(day.date)
    if (date) {
      acc.push({ date, nonWorking: day.is_non_working })
    }
    return acc
  }, [])

  return { rows, bars, dayFlags }
}

/** dd/MM/yyyy from the API to ISO for the timeline. Split, not parsed — see LeaveOverlapPanel. */
function toIso(value?: string | null): string | undefined {
  if (!value) return undefined
  const [day, month, year] = value.split("/")
  if (!day || !month || !year) return undefined
  const iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
  return dayjs(iso, ISO).isValid() ? iso : undefined
}

export default LeaveCalendar
